import type { DepopIntegration } from "src/interfaces/plaidIntegration.interface";
import { DirectImportSyncLockTtlMs } from "../syncLock";
import type { ResumeSyncStatus } from "../../types";
import type { SyncLockOptions } from "../syncLock";
import {
  discardAbandonedResume,
  getAbandonedResumeStatus,
  getActiveResumeLeaseExpiresAt,
  type AbandonedResumeInput,
} from "../resumeOffer";

const DepopDiscardFields = {
  salesResumeField: "depopSalesImportResume",
  inventoryResumeField: "depopInventoryImportResume",
  ttlMs: DirectImportSyncLockTtlMs,
} as const;

function toDepopResumeInput(
  integration: DepopIntegration,
  now = Date.now()
): AbandonedResumeInput {
  return {
    integrationId: integration.id,
    salesIsRunning: integration.salesIsRunning,
    inventoryIsRunning: integration.inventoryIsRunning,
    salesResume: integration.depopSalesImportResume,
    inventoryResume: integration.depopInventoryImportResume,
    ttlMs: DirectImportSyncLockTtlMs,
    platformLabel: "Depop",
    now,
  };
}

export function getDepopAbandonedResumeStatus(
  integration: DepopIntegration,
  now = Date.now()
): ResumeSyncStatus | undefined {
  return getAbandonedResumeStatus(toDepopResumeInput(integration, now));
}

/**
 * When a Depop checkpoint sits under a still-fresh lease, the wall-clock time
 * that lease expires (`*IsRunning + DirectImportSyncLockTtlMs`).
 */
export function getDepopActiveResumeLeaseExpiresAt(
  integration: DepopIntegration,
  now = Date.now()
): number | undefined {
  return getActiveResumeLeaseExpiresAt(toDepopResumeInput(integration, now));
}

/**
 * Dismissing the resume offer: drop the checkpoint plus the stale lease
 * (`*IsRunning` and its owner) so the offer is not re-derived from the
 * integration doc. No-op while a lease is still fresh (sync running elsewhere).
 */
export async function discardDepopResume(
  integrationId: string,
  job: ResumeSyncStatus["job"],
  options: Pick<SyncLockOptions, "getDb" | "now"> = {}
): Promise<boolean> {
  return await discardAbandonedResume(
    integrationId,
    job,
    DepopDiscardFields,
    options
  );
}
